/**
 * MaintenanceGate — the single decision point for maintenance UI.
 *
 *   active   → full takeover (unless this operator has bypass)
 *   active + operator bypass → thin "you are seeing the live platform" strip,
 *              so an admin verifying the release never mistakes it for normal
 *   scheduled → dismissible pre-announcement banner above the app
 *   off       → nothing, zero overhead
 *
 * Mounted once at the router root so it covers every route, including the
 * unauthenticated ones (a locked-out user may not even be able to log in).
 */

import type { ReactElement, ReactNode } from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { Trans } from "@lingui/react/macro";

import { MaintenanceBanner } from "./MaintenanceBanner";
import { MaintenanceScreen } from "./MaintenanceScreen";
import { useMaintenance } from "./useMaintenance";

export function MaintenanceGate({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const { record, isBlocking, isAnnounced, isOperatorBypass } = useMaintenance();

  if (isBlocking && record) {
    return <MaintenanceScreen record={record} />;
  }

  return (
    <>
      {isOperatorBypass ? (
        <motion.div
          role="status"
          initial={{ y: -32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
          className="relative z-30 flex items-center justify-center gap-2 border-accent/25 border-b bg-ando-orange-soft/50 px-4 py-1.5 text-[11.5px] text-ink-1"
        >
          <ShieldCheck size={12} className="text-accent" aria-hidden />
          <Trans>
            Manutenzione attiva per gli utenti — tu stai vedendo la piattaforma
            reale
          </Trans>
        </motion.div>
      ) : null}

      {isAnnounced && record ? <MaintenanceBanner record={record} /> : null}

      {children}
    </>
  );
}
